/** Peak / dyno power measurement location. Keep in sync with `@bilt/formatting`. */
export type PowerMeasurement = 'wheel' | 'crank';

export function normalizePowerMeasurement(
  value: unknown
): PowerMeasurement | null {
  if (value === 'wheel' || value === 'crank') return value;
  if (typeof value === 'string') {
    const v = value.trim().toLowerCase();
    if (v === 'wheel' || v === 'crank') return v;
  }
  return null;
}

/** Short unit for UI: wheel → WHP, crank/unknown → HP */
export function peakPowerUnit(
  measurement: PowerMeasurement | null | undefined
): 'WHP' | 'HP' {
  return measurement === 'wheel' ? 'WHP' : 'HP';
}

/** Lowercase unit suffix: "whp" | "hp" */
export function peakPowerUnitLower(
  measurement: PowerMeasurement | null | undefined
): 'whp' | 'hp' {
  return measurement === 'wheel' ? 'whp' : 'hp';
}

/** Ledger / card label (e.g. "WHP", "HP"). */
export function peakPowerLabel(
  measurement: PowerMeasurement | null | undefined
): string {
  return peakPowerUnit(measurement);
}

/** Full prose label for forms/export. */
export function peakPowerProseLabel(
  measurement: PowerMeasurement | null | undefined
): string {
  return measurement === 'wheel' ? 'Wheel horsepower' : 'Horsepower';
}

/** Format a peak power value with unit, e.g. "412 WHP". */
export function formatPeakPower(
  value: number | null | undefined,
  measurement: PowerMeasurement | null | undefined
): string | null {
  if (value == null || !Number.isFinite(Number(value))) return null;
  return `${value} ${peakPowerUnit(measurement)}`;
}

/**
 * Format dyno primary power using measurement location.
 * kw stays kw; hp becomes whp/hp from wheel/crank.
 */
export function formatDynoPowerDisplay(
  value: number | null | undefined,
  unitPrimary: string | null | undefined,
  measurement: PowerMeasurement | null | undefined
): string | null {
  if (value == null || !Number.isFinite(Number(value))) return null;
  const unit = (unitPrimary || 'hp').toLowerCase();
  if (unit === 'kw') return `${value} kw`;
  return `${value} ${peakPowerUnitLower(measurement)}`;
}

/** Dyno highlight line for share cards: horsepower plus torque when present. */
export function formatShareCardDynoTitle(record: {
  display_value?: string | null;
  title?: string | null;
  record_type_label?: string | null;
  value_primary?: number | null;
  unit_primary?: string | null;
  measurement_location?: PowerMeasurement | null;
  value_secondary?: number | null;
  unit_secondary?: string | null;
}): string {
  const power =
    formatDynoPowerDisplay(
      record.value_primary,
      record.unit_primary,
      record.measurement_location
    ) ||
    record.display_value ||
    record.title ||
    record.record_type_label ||
    'Dyno';
  if (record.value_secondary == null || !Number.isFinite(Number(record.value_secondary))) {
    return power;
  }
  const torqueUnit = (record.unit_secondary || 'lb-ft').trim() || 'lb-ft';
  return `${power} · ${record.value_secondary} ${torqueUnit}`;
}
